/**
 * Server-timed races (act-babylon DR-0093; ACT HQ BATTLE-PASS.md §3, t-939). The
 * race runs here, but the home game server stamps its start (at GO), each lap and
 * its end on its own clock, so the battle pass can pay the laps (Theo, 10 Oct
 * 2026: "just do it for laps") and the race counts on the ACT account. Each lap
 * carries a light proof of steering: how often the steer changed side, and how
 * long any input was held. Every call fails open: a race never waits on the server.
 */
import { resolveColyseusEndpoint } from "@act/client-core/network";
import type { VdRaceFinishResponse, VdRaceMode, VdRaceStartResponse } from "@act/shared";
import { pilot } from "./pilot";

/** The steer counts as on a side past this. */
const SIDE = 0.3;
const TIMEOUT_MS = 8000;

async function post<T>(path: string, body: unknown): Promise<T | null> {
  const token = await pilot.accessToken();
  if (!token) return null;
  try {
    const res = await fetch(`${resolveColyseusEndpoint()}${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
      cache: "no-store",
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

export class RaceTicket {
  private readonly ticket: Promise<string | null>;
  /** The lap calls in order: the server wants lap n after lap n - 1. */
  private sent: Promise<unknown> = Promise.resolve();
  private side = 0;
  private steerFlips = 0;
  private inputMs = 0;
  private done = false;

  constructor(course: string, ship: string, mode: VdRaceMode) {
    this.ticket = post<VdRaceStartResponse>("/vectordrift/race/start", { course, ship, mode }).then((r) => r?.ticket ?? null);
  }

  /** Each racing frame: count the steering and the time with input held. */
  sample(dt: number, steer: number, held: boolean): void {
    const side = steer > SIDE ? 1 : steer < -SIDE ? -1 : 0;
    if (side !== 0 && side !== this.side) {
      if (this.side !== 0) this.steerFlips += 1;
      this.side = side;
    }
    if (held) this.inputMs += dt * 1000;
  }

  /** The `n`th lap crossed. */
  lap(n: number): void {
    const proof = { steerFlips: this.steerFlips, inputMs: Math.round(this.inputMs) };
    this.steerFlips = 0;
    this.inputMs = 0;
    this.sent = this.sent.then(async () => {
      const ticket = await this.ticket;
      if (ticket) await post("/vectordrift/race/lap", { ticket, lap: n, ...proof });
    });
  }

  /** Every lap in. Resolves to the server's account of the race, or null offline. */
  finish(place: number | null): Promise<VdRaceFinishResponse | null> {
    this.done = true;
    return (this.sent = this.sent.then(async () => {
      const ticket = await this.ticket;
      return ticket ? post<VdRaceFinishResponse>("/vectordrift/race/finish", { ticket, place }) : null;
    })) as Promise<VdRaceFinishResponse | null>;
  }

  /** Left before the end (retry, back to the menu): the laps already checked still count. */
  quit(): void {
    if (this.done) return;
    this.done = true;
    this.sent = this.sent.then(async () => {
      const ticket = await this.ticket;
      if (ticket) await post("/vectordrift/race/quit", { ticket });
    });
  }
}
